import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import {
  Restaurant,
  Logout,
  Assignment
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/axios'

const formatNumeroBR = (v) => (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const formatBRL = (v) => `R$ ${formatNumeroBR(v)}`

// Entidade "fornecedor" de um item: o grupo/associação do produtor, se houver, senão o próprio produtor.
const fornecedorDoItem = (item) => (item.idgrupo ? (item.nome_grupo || item.produtor) : item.produtor)

const AgroeconomiaDashboard = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const [editais, setEditais] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchFornecimento()
  }, [])

  const fetchFornecimento = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/editais/fornecimento_educacao.php')
      if (response.data.success) {
        setEditais(response.data.editais || [])
      } else {
        setError(response.data.message || 'Erro ao carregar dados de fornecimento')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar dados de fornecimento')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Agroeconomia - Editais
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.nome}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, mt: 3, fontWeight: 600 }}>
          Gestão de Edital
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : editais.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum edital cadastrado
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {editais.map((edital) => (
              <Paper key={edital.id} elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h6">{edital.titulo}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(edital.data_inicio).toLocaleDateString('pt-BR')} a {new Date(edital.data_fim).toLocaleDateString('pt-BR')}
                    </Typography>
                  </Box>
                  <Chip
                    label={edital.status === 'finalizado' ? 'Finalizado' : 'Aberto'}
                    color={edital.status === 'finalizado' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                {edital.itens.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum fornecimento atribuído para este edital.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Produtor/Associação</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pago</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Motivo do não pagamento</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {edital.itens.map((item, index) => (
                          <TableRow key={`${item.concorrente_id}-${item.produto_id}-${index}`}>
                            <TableCell>{fornecedorDoItem(item)}</TableCell>
                            <TableCell>{item.produto}</TableCell>
                            <TableCell align="right">{formatBRL(item.valor_unitario)}</TableCell>
                            <TableCell align="right">
                              {formatNumeroBR(item.quantidade)}{item.unidademedida ? ` ${item.unidademedida}` : ''}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.pago === 1 ? 'Pago' : 'Não pago'}
                                color={item.pago === 1 ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{item.pago === 1 ? '' : (item.motivo_nao_pago || '-')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default AgroeconomiaDashboard
